import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import React, { useMemo, useState } from 'react';
import { MainDashboardElementType } from 'common/models/Dashboard/EditLayout';
import DialogContentText from '@mui/material/DialogContentText';

interface Props {
  open: boolean;
  closeHandler: () => void;
  addElementHandler: (type: MainDashboardElementType) => void;
}

interface AddOption {
  value: MainDashboardElementType;
  label: string;
  secondaryLabel?: string;
}

const availableElements: AddOption[] = [
  { value: 'Activity', label: 'Activity' },
  { value: 'CandidatePipeline', label: 'Candidate Pipeline' },
  { value: 'CurrentContractors', label: 'Current Contractors' },
  { value: 'Forecast', label: 'Forecast' },
  { value: 'HistoricalBillings', label: 'Historical Revenue' },
  { value: 'JobPipeline', label: 'Job Pipeline' },
  { value: 'KPI', label: 'KPI' },
  { value: 'LinkButton', label: 'Link Button' },
  { value: 'MyOpenJobs', label: 'Open Jobs' },
  { value: 'PlacementFees', label: 'Placement Fees' },
  { value: 'Ratio', label: 'Ratio' },
  { value: 'Revenue', label: 'Revenue' },
  { value: 'TopPerformers', label: 'Top Performers' },
  { value: 'UserInfo', label: 'User Info' },
];

export default function AddDashboardElementDialog({
  open,
  closeHandler,
  addElementHandler,
}: Props) {
  const [elementType, setElementType] = useState<MainDashboardElementType>('');

  const selectedElementType = useMemo(() => {
    if (elementType === '') return null;
    const type = availableElements.find((e) => e.value === elementType);
    return type ?? null;
  }, [elementType, availableElements]);

  return (
    <Dialog open={open} fullWidth>
      <DialogTitle sx={{ pb: 0 }}>Add new element</DialogTitle>
      <DialogContent>
        <DialogContentText>
          New element will be added at the bottom of your dashboard.
        </DialogContentText>
        <Box pt={2}>
          <Autocomplete
            size="small"
            value={selectedElementType}
            options={availableElements}
            onChange={(e, v) => setElementType(v ? v.value : '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Element Type"
                inputProps={{ ...params.inputProps, 'data-lpignore': 'true' }}
              />
            )}
            renderOption={(props, option) => (
              <ListItem {...props}>
                <ListItemText primary={option.label} secondary={option.secondaryLabel} />
              </ListItem>
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="error" onClick={closeHandler}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          disabled={elementType === ''}
          onClick={() => addElementHandler(elementType)}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
